import { Modes, Themes } from '@palmetto/dsm-tokens';
import React, { createContext, useContext, useEffect, useState } from 'react';

export interface ThemeContextShape {
  mode: Modes;
  theme: Themes;
}

export const ThemeContext = createContext<ThemeContextShape>({ mode: Modes.light, theme: Themes.palmetto });

export interface ThemeProviderProps {
  theme: Themes;
  mode?: Modes;
  children?: React.ReactNode;
}

const DARK_MODE_QUERY = window.matchMedia('(prefers-color-scheme: dark)');

export const ThemeProvider = ({ theme, mode: forcedMode, children }: ThemeProviderProps) => {
  const [systemMode, setSystemMode] = useState<Modes>(DARK_MODE_QUERY.matches ? Modes.dark : Modes.light);

  useEffect(() => {
    if (forcedMode !== undefined) return;
    const listener = (e: MediaQueryListEvent) => setSystemMode(e.matches ? Modes.dark : Modes.light);
    DARK_MODE_QUERY.addEventListener('change', listener);
    return () => DARK_MODE_QUERY.removeEventListener('change', listener);
  }, [forcedMode]);

  const mode = forcedMode ?? systemMode;

  return (
    <div data-mode={mode}>
      <div data-theme={theme}>
        <ThemeContext.Provider value={{ mode, theme }}>{children}</ThemeContext.Provider>
      </div>
    </div>
  );
};

export const useThemeContext = (): ThemeContextShape => useContext(ThemeContext);
